import moment from 'moment';
import { Loan } from '../../../types/loan/Loan';
import { LoanStatus } from '../../../types/loan/LoanStatus';
import { LoanReviewStatus } from '../../../types/loan/LoanReviewStatus';
import { LoanCurrency } from '../../../types/loan/LoanCurrency';
import { LoanRefIndex } from '../../../types/enums/loan/LoanRefIndex';
import { DataroomProvider } from '../../../types/enums/loan/DataroomProvider';
import { LoanFitchRating, LoanMoodysRating, LoanSnpRating } from '../../../types/enums/loan/LoanRatings';
import { SaveLoanContactModel, SaveLoanModel } from '../../../types/loan/SaveLoanModel';
import { moneyUtils } from '../../../utils/money.utils';
import { formatUtils } from '../../../utils/format.utils';
import { numericUtils } from '../../../utils/numeric.utils';

export interface LoanFormValues {
    issuerName: string;
    status: LoanStatus;
    arrangerReferenceName?: string;
    size?: number | string;
    addOn?: number | string;
    review?: LoanReviewStatus | null;
    dueDate?: string | null;
    dueTime?: string | null;
    maturity?: string | null;
    termYears?: number;
    pricingDate?: string | null;
    callProtection?: string | null;
    lenderCallDate?: string | null;
    lenderCallTime?: string | null;
    lenderCallLiveDomestic?: string | null;
    lenderCallLiveInternational?: string | null;
    lenderCallLivePasscode?: string | null;
    lenderCallReplayDomestic?: string | null;
    lenderCallReplayInternational?: string | null;
    lenderCallReplayPasscode?: string | null;
    dataroomProvider?: DataroomProvider | null;
    dataroomLink?: string | null;
    facility?: string | null;
    currencyCode?: LoanCurrency | null;
    covLite?: boolean | null;
    issuerDescription?: string | null;
    useOfProceedsDescription?: string | null;
    industry?: string | null;
    figi?: string | null;
    bbmgId?: string | null;
    loanXId?: string | null;
    guidanceMin?: number | string;
    guidanceMax?: number | string;
    priceMin?: number | string;
    priceMax?: number | string;
    floor?: number | string;
    margin?: number | string;
    refIndex?: LoanRefIndex | null;
    trancheRatingMoodys?: LoanMoodysRating | null;
    trancheRatingSnP?: LoanSnpRating | null;
    trancheRatingFitch?: LoanFitchRating | null;
    cfRatingMoodys?: LoanMoodysRating | null;
    cfRatingSnP?: LoanSnpRating | null;
    cfRatingFitch?: LoanFitchRating | null;
    coArrangers: string[];
    useOfProceeds: string[];
}

export function buildInitialLoanFormValues(loan?: Loan): LoanFormValues {
    if (!loan) {
        return {
            issuerName: '',
            status: LoanStatus.Draft,
            arrangerReferenceName: '',
            size: undefined,
            addOn: undefined,
            review: null,
            coArrangers: [],
            useOfProceeds: [],
            covLite: false,
        };
    }

    return {
        issuerName: loan.issuerName || '',
        status: loan.status,
        arrangerReferenceName: loan.arrangerReferenceName || undefined,
        size: numericUtils.isNumber(loan.size) ? moneyUtils.amountToMM(loan.size) : undefined,
        addOn: loan.addOn ? moneyUtils.amountToMM(loan.addOn) : undefined,
        review: loan.review ?? null,
        dueDate: loan.dueDate ?? null,
        dueTime: loan.dueDate ?? null,
        maturity: loan.maturity ?? null,
        termYears: loan.termYears,
        pricingDate: loan.pricingDate ?? null,
        callProtection: loan.callProtection ?? null,
        lenderCallDate: loan.lenderCallDate ?? null,
        lenderCallTime: loan.lenderCallDate ?? null,
        lenderCallLiveDomestic: loan.lenderCallLiveDomestic ?? null,
        lenderCallLiveInternational: loan.lenderCallLiveInternational ?? null,
        lenderCallLivePasscode: loan.lenderCallLivePasscode ?? null,
        lenderCallReplayDomestic: loan.lenderCallReplayDomestic ?? null,
        lenderCallReplayInternational: loan.lenderCallReplayInternational ?? null,
        lenderCallReplayPasscode: loan.lenderCallReplayPasscode ?? null,
        dataroomProvider: loan.dataroomProvider ?? null,
        dataroomLink: loan.dataroomLink ?? null,
        facility: loan.facility ?? null,
        currencyCode: loan.currencyCode ?? null,
        covLite: loan.covLite ?? false,
        issuerDescription: loan.issuerDescription ?? null,
        useOfProceedsDescription: loan.useOfProceedsDescription ?? null,
        industry: loan.industry ?? null,
        figi: loan.figi ?? null,
        bbmgId: loan.bbmgId ?? null,
        loanXId: loan.loanXId ?? null,
        guidanceMin: formatUtils.formatDecimal(loan.guidanceMin, 1),
        guidanceMax: formatUtils.formatDecimal(loan.guidanceMax, 1),
        priceMin: formatUtils.formatDecimal(loan.priceMin, 3),
        priceMax: formatUtils.formatDecimal(loan.priceMax, 3),
        floor: formatUtils.formatDecimal(loan.floor, 2),
        margin: formatUtils.formatDecimal(loan.margin, 3),
        refIndex: loan.refIndex ?? null,
        trancheRatingMoodys: loan.trancheRatingMoodys ?? null,
        trancheRatingSnP: loan.trancheRatingSnP ?? null,
        trancheRatingFitch: loan.trancheRatingFitch ?? null,
        cfRatingMoodys: loan.cfRatingMoodys ?? null,
        cfRatingSnP: loan.cfRatingSnP ?? null,
        cfRatingFitch: loan.cfRatingFitch ?? null,
        coArrangers: loan.coArrangers ?? [],
        useOfProceeds: loan.useOfProceeds ?? [],
    };
}

export function toIsoDateString(value?: string | null): string | null {
    if (!value) return null;
    const m = moment.utc(value);
    if (!m.isValid()) return null;
    return m.startOf('day').toISOString();
}

export type TermYearsCalculation =
    | { value: number; disabledReason?: undefined }
    | { value?: undefined; disabledReason: string };

export function calculateTermYears(dueDate?: string | null, maturity?: string | null): TermYearsCalculation {
    const due = dueDate ? moment.utc(dueDate) : null;
    const mat = maturity ? moment.utc(maturity) : null;
    if (!due?.isValid() || !mat?.isValid()) {
        return { disabledReason: 'Fill in Due Date and Maturity to calculate the term' };
    }
    const days = mat.startOf('day').diff(due.startOf('day'), 'days');
    if (days < 0) {
        return { disabledReason: 'Maturity must not be earlier than Due Date' };
    }
    return { value: numericUtils.round(days / 365.25, 2) };
}

export function combineDateAndTime(date?: string | null, time?: string | null): string | null {
    if (!date) return null;
    const d = moment.utc(date);
    if (!d.isValid()) return null;
    const dateOnly = d.startOf('day');
    if (!time) return dateOnly.toISOString();
    const t = moment.utc(time);
    if (!t.isValid()) return dateOnly.toISOString();
    return dateOnly.set({
        hour: t.hour(),
        minute: t.minute(),
        second: 0,
        millisecond: 0,
    }).toISOString();
}

export function buildSavePayload(values: LoanFormValues, contacts: SaveLoanContactModel[], status: LoanStatus): SaveLoanModel {
    const millionMultiplier = 1.0e+6;

    const sizeRaw = typeof values.size === 'string' ? values.size.replace(/,/g, '') : values.size;
    const size = numericUtils.numberOrDefault(sizeRaw, null);

    const addOnRaw = typeof values.addOn === 'string' ? values.addOn.replace(/,/g, '') : values.addOn;
    const addOn = numericUtils.numberOrDefault(addOnRaw, null);

    return {
        issuerName: values.issuerName?.trim() || '',
        status,
        arrangerReferenceName: values.arrangerReferenceName || null,
        size: size == null ? null : size * millionMultiplier,
        addOn: addOn == null ? null : addOn * millionMultiplier,
        review: values.review ?? null,
        dueDate: combineDateAndTime(values.dueDate, values.dueTime),
        maturity: toIsoDateString(values.maturity),
        termYears: values.termYears ?? null,
        termDate: toIsoDateString(values.maturity),
        pricingDate: toIsoDateString(values.pricingDate),
        callProtection: values.callProtection || null,
        lenderCallDate: combineDateAndTime(values.lenderCallDate, values.lenderCallTime),
        lenderCallLiveDomestic: values.lenderCallLiveDomestic?.trim() || null,
        lenderCallLiveInternational: values.lenderCallLiveInternational?.trim() || null,
        lenderCallLivePasscode: values.lenderCallLivePasscode?.trim() || null,
        lenderCallReplayDomestic: values.lenderCallReplayDomestic?.trim() || null,
        lenderCallReplayInternational: values.lenderCallReplayInternational?.trim() || null,
        lenderCallReplayPasscode: values.lenderCallReplayPasscode?.trim() || null,
        dataroomProvider: values.dataroomProvider || null,
        dataroomLink: values.dataroomLink?.trim() || null,
        facility: values.facility || null,
        currencyCode: values.currencyCode ?? null,
        covLite: values.covLite ?? false,
        issuerDescription: values.issuerDescription || null,
        useOfProceedsDescription: values.useOfProceedsDescription || null,
        industry: values.industry || null,
        figi: values.figi?.trim() || null,
        bbmgId: values.bbmgId?.trim() || null,
        loanXId: values.loanXId?.trim() || null,
        guidanceMin: numericUtils.numberOrDefault(values.guidanceMin, null),
        guidanceMax: numericUtils.numberOrDefault(values.guidanceMax, null),
        priceMin: numericUtils.numberOrDefault(values.priceMin, null),
        priceMax: numericUtils.numberOrDefault(values.priceMax, null),
        floor: numericUtils.numberOrDefault(values.floor, null),
        margin: numericUtils.numberOrDefault(values.margin, null),
        refIndex: values.refIndex ?? null,
        trancheRatingMoodys: values.trancheRatingMoodys ?? null,
        trancheRatingSnP: values.trancheRatingSnP ?? null,
        trancheRatingFitch: values.trancheRatingFitch ?? null,
        cfRatingMoodys: values.cfRatingMoodys ?? null,
        cfRatingSnP: values.cfRatingSnP ?? null,
        cfRatingFitch: values.cfRatingFitch ?? null,
        coArrangers: values.coArrangers ?? [],
        useOfProceeds: values.useOfProceeds ?? [],
        contacts: contacts.map(c => ({
            id: c.id ?? null,
            firstName: c.firstName?.trim(),
            lastName: c.lastName?.trim(),
            desk: c.desk?.trim(),
            title: c.title?.trim(),
            email: c.email?.trim(),
            officePhone: c.officePhone?.trim(),
            mobilePhone: c.mobilePhone?.trim(),
            linkedIn: c.linkedIn?.trim(),
        })),
    };
}
